import { produce } from 'immer';
import {
  makeSyncReduxWithLocalStorageObserver,
  restoreFromLocalStorage,
} from 'app/dataManagement/syncReduxStateWithLocalStorage';
import {
  XRAY_CONFIGURATION_ADD_OPERATOR,
  XRAY_CONFIGURATION_TOGGLE_TELEMETRY,
  XRAY_CONFIGURATION_DELETE_OPERATOR,
  XRAY_CONFIGURATION_SET_DEVICE_PATH,
  XRAY_CONFIGURATION_SET_LAST_OPERATORS,
  XRAY_CONFIGURATION_UPDATE_ACQUISITION_CONSTANT,
  XrayGeneratorConfigurationAction,
  XRAY_CONFIGURATION_IMPORT_ACQUISITION_CONSTANT,
  XRAY_CONFIGURATION_TOGGLE_DIRECT_COMMUNICATION_MODE,
} from 'app/redux/xrayGeneratorConfiguration/actions';
import observeStore from 'app/utils/redux/observeStore';
import { Store } from 'redux';
import ACQUISITION_CONSTANTS, {
  AcquisitionConstantsByProfile,
} from 'app/utils/acquisitionConstants';
import _ from 'lodash';
import { SHARED_USER_CONFIG_LOADED } from 'app/redux/sharedUserConfig/actions';

export const XRAY_GENERATOR_LOCAL_STORAGE_KEY = 'redux.xray_generator';

export type XRayGeneratorConfigurationState = {
  devicePath?: string;
  isTelemetry: boolean;
  isDirectCommunicationMode?: boolean;
  operators?: string[];
  selected_operators?: string[];
  acquisitionConstants: AcquisitionConstantsByProfile;
};

const configurationInitialState = (): XRayGeneratorConfigurationState => ({
  isTelemetry: false,
  operators: [],
  selected_operators: [],
  acquisitionConstants: ACQUISITION_CONSTANTS,
  isDirectCommunicationMode: false,
  ...restoreFromLocalStorage(XRAY_GENERATOR_LOCAL_STORAGE_KEY, {}),
});

const xrayConfigurationReducer = (
  xRayConfiguration: XRayGeneratorConfigurationState = configurationInitialState(),
  // @ts-ignore
  { type, payload }: XrayGeneratorConfigurationAction
) => {
  return produce(xRayConfiguration, (draft) => {
    switch (type) {
      case XRAY_CONFIGURATION_SET_DEVICE_PATH:
        draft.devicePath = payload.devicePath;
        break;
      case XRAY_CONFIGURATION_TOGGLE_TELEMETRY:
        draft.isTelemetry = payload.isTelemetry;
        break;
      case XRAY_CONFIGURATION_TOGGLE_DIRECT_COMMUNICATION_MODE:
        draft.isDirectCommunicationMode = payload.isDirectCommunicationMode;
        break;
      case XRAY_CONFIGURATION_ADD_OPERATOR:
        draft.operators = Array.from(new Set([...(draft.operators || []), payload.operator]));
        break;
      case XRAY_CONFIGURATION_SET_LAST_OPERATORS:
        draft.selected_operators = payload.operators;
        break;
      case XRAY_CONFIGURATION_DELETE_OPERATOR:
        draft.operators = Array.from(
          new Set(
            [...((draft.operators || []) as string[])].filter(
              (operator) => operator !== payload.operator
            )
          )
        );
        break;
      case XRAY_CONFIGURATION_UPDATE_ACQUISITION_CONSTANT: {
        const { profile, acquisitionConstantsList } = payload;
        // Remove all constants specified for a sub profile of the specified profile
        // e.g. if profile is 'abdomen', remove all constants for 'abodmen abdomen' and 'abdomen bassin'
        // Special case for default where all constants should be removed
        Object.keys(draft.acquisitionConstants)
          .filter((key) => key.startsWith(profile) || profile === 'default')
          .forEach((key) => {
            delete draft.acquisitionConstants[key];
          });
        draft.acquisitionConstants[profile] = acquisitionConstantsList;

        break;
      }
      case XRAY_CONFIGURATION_IMPORT_ACQUISITION_CONSTANT: {
        const { acquisitionConstantsProfiles } = payload;
        draft.acquisitionConstants = _.cloneDeep(acquisitionConstantsProfiles);

        break;
      }
      case SHARED_USER_CONFIG_LOADED:
        if (payload.acquisitionConstants && !_.isEmpty(payload.acquisitionConstants)) {
          draft.acquisitionConstants = payload.acquisitionConstants;
        }
        break;
    }
  });
};

export type StoreWithXRaygeneratorconfiguration = {
  xRayConfiguration: XRayGeneratorConfigurationState;
};

export const selectXRayConfiguration = (state: StoreWithXRaygeneratorconfiguration) =>
  state.xRayConfiguration;
export const selectXRayDevicePath = (state: StoreWithXRaygeneratorconfiguration) =>
  selectXRayConfiguration(state).devicePath;
export const selectTelemetry = (state: StoreWithXRaygeneratorconfiguration) =>
  selectXRayConfiguration(state).isTelemetry;
export const selectIsDirectCommunicationMode = (state: StoreWithXRaygeneratorconfiguration) =>
  selectXRayConfiguration(state).isDirectCommunicationMode;
export const selectXRayOperators = (state: StoreWithXRaygeneratorconfiguration) =>
  selectXRayConfiguration(state).operators;
export const selectXRaySelectedOperators = (state: StoreWithXRaygeneratorconfiguration) =>
  selectXRayConfiguration(state).selected_operators;
export const selectAcquisitionConstants = (state: StoreWithXRaygeneratorconfiguration) =>
  selectXRayConfiguration(state).acquisitionConstants;

export const attachXRayConfigurationWithLocalStorageSynchronizationObserver = <
  S extends StoreWithXRaygeneratorconfiguration
>(
  store: Store<S>
) => {
  observeStore(
    store,
    selectXRayConfiguration,
    makeSyncReduxWithLocalStorageObserver(XRAY_GENERATOR_LOCAL_STORAGE_KEY)
  );
};

export default xrayConfigurationReducer;
